import React, { FC } from 'react';

import styled from 'styled-components';
import { Botao } from '../Botao/Botao';
import Spinner from '@atlaskit/spinner';
import { OrdemColunaIcone } from './OrdemColunaIcone';
import { AriaSort, OrdemColunaTabela } from './model/OrdemColunaTabela';
import { Coluna } from './model/Coluna';
import { ItemTabelaComponentProps } from './model/ItemTabelaComponentProps';

import './tabela.css'


interface TabelaProps<T> {
  colunas: Coluna[];
  dados: T[];
  totalElementos?: number;
  retry?: () => void;
  error?: Error;

  colunaConfig?: OrdemColunaTabela;
  classNameTabela?: string;

  loading: boolean;

  ItemTabelaComponent: React.ComponentType<ItemTabelaComponentProps<T>>;
  onChangeSort?: (colunaOrdenada: OrdemColunaTabela) => void;
  dadosKeyExtractor: (item: T, indice: number) => string;
}

export const Tabela = <T extends unknown>({classNameTabela, colunas, dados, totalElementos, ItemTabelaComponent, colunaConfig: ordem, onChangeSort, loading, error, retry, dadosKeyExtractor}: TabelaProps<T>) => {
  const ordemCorrente = ordem ?? { coluna: 'nenhuma', ordem: AriaSort.None };
  const colunasElements = colunas.map(
    (coluna, indice) => 
      <TH key={indice} 
          className={`${coluna.textAlign}`}
          coluna={coluna}
          ordem={ordemCorrente}
          onChangeSort={onChangeSort} />
  );

  const linhasElements: JSX.Element[] = [];
  // if (loading && dados.length > 0) {
  //   linhasElements.push(<LinhaUnicaColuna key="carregando" totalColunas={colunasElements.length}>Carregando</LinhaUnicaColuna>);
  // }
  if (!loading && !error && dados.length === 0) {
    linhasElements.push(<LinhaUnicaColuna key="sem-resultados" totalColunas={colunasElements.length}>Nenhum resultado encontrado</LinhaUnicaColuna>);
  } else if (loading && dados.length === 0) {
    linhasElements.push(<LinhaUnicaColuna key="sem-resultados" totalColunas={colunasElements.length}><Spinner /> Carregando</LinhaUnicaColuna>);
  } else if (error) {
    linhasElements.push(
      <LinhaUnicaColuna key="tentar-novamentesem" totalColunas={colunasElements.length}>
        Ocorreu um erro ao tentar obter os dados
        {retry && <Botao type="button" onClick={() => retry()}>Tentar novamente</Botao>}
      </LinhaUnicaColuna>
    );
  } else {
    dados.map((dado, indice) => linhasElements.push(<ItemTabelaComponent key={dadosKeyExtractor(dado, indice)} item={dado} />))
  }

  const classNameBody = loading ? 'loading' : '';

  return (
    <>
    <p>Total: {totalElementos}</p>
    <table className={`tabela ${classNameTabela ?? ''}`}>
      <thead>
        <tr>{ colunasElements }</tr>
      </thead>
      <tbody className={classNameBody}>{ linhasElements }</tbody>
    </table>
    </>
  );
}

interface THProps {
  className: string;
  coluna: Coluna;
  ordem: OrdemColunaTabela;
  onChangeSort?: (colunaOrdenada: OrdemColunaTabela) => void;
}

const TH: FC<THProps> = props => {
  return props.coluna.ordernavel && props.onChangeSort !== undefined
    ? <THOrdenavel {...props} />
    : <THNaoOrdenavel {...props} />
}

const THNaoOrdenavel: FC<THProps> = ({className, coluna}) => {
  return (
    <th className={coluna.className ?? coluna.textAlign}>
      <span>{coluna.nome}</span> 
    </th>
  )
}

interface LinhaUnicaColunaProps {
  totalColunas: number;
}

const LinhaUnicaColuna: FC<LinhaUnicaColunaProps> = props => {
  return <tr><LinhaUnicaColunaTD colSpan={props.totalColunas}>{props.children}</LinhaUnicaColunaTD></tr>
}

const LinhaUnicaColunaTD = styled.td`
  text-align: center;
  padding: 1.5rem 0;
`;

const THOrdenavel: FC<THProps> = ({className, coluna, ordem, onChangeSort}) => {
  const nomeAtributoOrdenacao = (coluna.atributoOrdenacao !== null && coluna.atributoOrdenacao !== undefined) ? coluna.atributoOrdenacao! : coluna.nome;
  const classNameOrdenavel = coluna.ordernavel === true ? "ordenavel" : "";

  const ariaSort = nomeAtributoOrdenacao === ordem.coluna ? ordem.ordem : AriaSort.None;
  const title = `Ordenar por: ${coluna.nome}`;

  const onClick = () => {
    if (onChangeSort !== undefined) {
      const novaOrdenacao = gerarNovaOrdenacao(nomeAtributoOrdenacao, ordem);
      onChangeSort(novaOrdenacao)
    }
  }

  return (
    <th className={`${className} ${classNameOrdenavel}`} aria-sort={ariaSort} onClick={onClick}>
      <span role="button" title={title}>{coluna.nome}</span>
      <OrdemColunaIcone ariaSort={ariaSort} aria-hidden={true} />
    </th>
  )
}

function gerarNovaOrdenacao(nomeAtributoOrdenacao: string, colunaConfig: OrdemColunaTabela) {
  if (nomeAtributoOrdenacao === colunaConfig.coluna) {
    if (colunaConfig.ordem === "ascending") {
      colunaConfig.ordem = AriaSort.Descending;
    } else if (colunaConfig.ordem === "descending") {
      colunaConfig.ordem = AriaSort.None;
    } else {
      colunaConfig.ordem = AriaSort.Ascending;
    }
  } else {
    colunaConfig.coluna = nomeAtributoOrdenacao;
    colunaConfig.ordem = AriaSort.Ascending;
  }
  
  return {...colunaConfig};
}
